import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IconChevronRight } from "@tabler/icons-react";
import { Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { BackButton } from "@/components/button";
import { ComponentLoader } from "@/components/loader";
import { useAppState } from "@/modules/app/useAppContext";
import { PermissionsEnum } from "@/helpers/constants";
import { getIntegrationByID } from "@/helpers/apis";
import { DbtCoreConnectionCard } from "./DbtCoreConnectionCard";
import { DbtCoreInstructionsCard } from "./DbtCoreInstructionsCard";
import { DbtCoreSyncHistory } from "./DbtCoreSyncHistory";

const DBT_CORE_QUERY_KEYS = {
  INTEGRATION: "dbt-core-integration",
} as const;

const DbtCoreDetails = (): JSX.Element => {
  const { integrationId, environment } = useParams<{
    integrationId: string;
    environment: string;
  }>();
  const { loggedinUser: user } = useAppState();
  const syncOpAllowed = Boolean(user?.permission?.includes(PermissionsEnum.DATASTORE_EDIT));

  const { data: integration, isLoading } = useQuery({
    queryKey: [DBT_CORE_QUERY_KEYS.INTEGRATION, integrationId, environment],
    queryFn: () => getIntegrationByID(integrationId!, environment!),
    enabled: Boolean(integrationId && environment),
  });

  if (isLoading) {
    return <ComponentLoader label="Loading integration details..." />;
  }

  if (!integration) {
    return (
      <Stack direction="column" align="center" justify="center" className="al-h-full">
        <Typography className="al-text-muted-foreground">Integration not found</Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" className="al-overflow-y-auto al-h-full">
      <Stack direction="row" align="center" gap="sm">
        <BackButton defaultUrl="/settings/datastores" />
        <Stack direction="row" align="center" gap="xs" className="al-font-semibold al-text-lg">
          <Link to="/settings/datastores" className="al-text-muted-foreground">
            Connections
          </Link>
          <IconChevronRight size={16} className="al-text-muted-foreground" />
          <Typography weight="regular">{integration.name}</Typography>
        </Stack>
      </Stack>

      <Stack direction="column" gap="md" className="al-mt-4">
        <Stack direction="row" gap="md" className="al-flex-wrap lg:al-flex-nowrap">
          <div className="al-w-full lg:al-w-1/3">
            <DbtCoreConnectionCard
              integration={integration}
              syncOpAllowed={syncOpAllowed}
            />
          </div>
          <div className="al-w-full lg:al-w-2/3">
            <DbtCoreInstructionsCard integration={integration} />
          </div>
        </Stack>
        <DbtCoreSyncHistory syncHistory={integration.sync_history} />
      </Stack>
    </Stack>
  );
};

export default DbtCoreDetails;
